import React, { useState, useEffect } from "react";
import { FileText, Plus, Edit, X, CheckCircle, Clock, AlertCircle, Eye, RefreshCw, Check, Printer } from "lucide-react";
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

const UnderSection21 = () => {
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showSection21, setShowSection21] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [isProcessingDecision, setIsProcessingDecision] = useState(false);
  const { language, translations } = useLanguage();
  
  const [owners, setOwners] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState("");
  const [ownerMessage, setOwnerMessage] = useState("");
  const [proposalData, setProposalData] = useState({});
  const [loadingRecords, setLoadingRecords] = useState(false);

  // LAO Decision states
  const [laoDecision, setLaoDecision] = useState('');
  const [selectedDecisionDate, setSelectedDecisionDate] = useState('');
  const [laoRemarks, setLaoRemarks] = useState('');

  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const [fileUrlToView, setFileUrlToView] = useState(null);
  const [isPDFToView, setIsPDFToView] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);

  // User role
  const [userRole, setUserRole] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const user = JSON.parse(userData);
        return user.user_role || null;
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
    return null;
  });

  // Form state for claim
  const [formData, setFormData] = useState({
    claimant_name: '',
    claimant_address: '',
    claim_date: '',
    survey_no: '',
    gateNo: '',
    claim_details: '',
    dist_name: '',
    taluka_name: '',
    village_name: '',
    document: null
  });

  // Auto-hide notifications after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // ---- Proposal ID ----
  useEffect(() => {
    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
      if (currentProposal) {
        try {
          const proposalId = currentProposal?.data?.default_proposal ||
            currentProposal?.data?.proposal_id ||
            currentProposal?.default_proposal ||
            currentProposal?.proposal_id ||
            null;

          return { proposalId };
        } catch (e) {
          console.error('Error parsing current project data (getProposalInfo):', e);
        }
      }
      return { proposalId: null };
    };

    const proposalInfo = getProposalInfo();
    setCurrentProposalId(proposalInfo.proposalId);
  }, []);

  // ---- Project ID ----
  useEffect(() => {
    const getSelectedProject = () => {
      try {
        const storedProject = localStorage.getItem('selectedProject');
        if (storedProject) {
          const project = JSON.parse(storedProject);
          const projectIdFromStorage = project.id || project.project_id;

          if (projectIdFromStorage && projectIdFromStorage !== currentProjectId) {
            setCurrentProjectId(projectIdFromStorage);
          }
        }
      } catch (error) {
        console.error('Error reading selected project:', error);
      }
    };

    getSelectedProject();
    window.addEventListener('storage', getSelectedProject);
    return () => window.removeEventListener('storage', getSelectedProject);
  }, [currentProjectId]);

  //  ---- STRICT Fetch Gat No. & Filter by Village ----
  useEffect(() => {
    const fetchOwners = async () => {
      if (formData.gateNo && currentProjectId && currentProposalId && formData.village_name) {
        try {
          const res = await apiService.getVerifyOwnerSurveyNo(
            formData.gateNo,
            currentProjectId,
            currentProposalId
          );

          if (res?.status === 200 && res?.verify && Array.isArray(res.data)) {
            // STRICT FILTER: Check if the village_name from the API matches the form's village_name
            const villageSpecificOwners = res.data.filter(
              (apiOwner) => apiOwner.village_name === formData.village_name
            );

            if (villageSpecificOwners.length > 0) {
              setOwners(villageSpecificOwners);
              setOwnerMessage(""); 
            } else {
              setOwners([]);
              setOwnerMessage(`गट क्रमांक ${formData.gateNo} साठी ${formData.village_name} गावात कोणतेही मालक सापडले नाहीत.`);
            }
          } else {
            setOwners([]);
            setOwnerMessage(`गट क्रमांक ${formData.gateNo} साठी ${formData.village_name} गावात कोणतेही मालक सापडले नाहीत.`);
          }
        } catch (err) {
          console.error("Error fetching owners:", err);
          setOwners([]);
          setOwnerMessage("Failed to load owner data.");
        }
      } else {
         setOwners([]);
         setOwnerMessage("");
      }
    };

    // Debounce to prevent API spam while typing
    const timeoutId = setTimeout(() => {
      fetchOwners();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.gateNo, formData.village_name, currentProjectId, currentProposalId]);

  const handleOwnerSelect = (e) => {
    const ownerId = e.target.value;
    setSelectedOwnerId(ownerId);

    const owner = owners.find(o => o.land_owner_id.toString() === ownerId);
    if (owner) {
      setFormData(prev => ({
        ...prev,
        claimant_name: owner.owner_name || "",
        claimant_address: owner.owner_address || ""
      }));
    }
  };

  // Fetch Section 21 Data
  const fetchClaims = async () => {
    if (!currentProjectId || !currentProposalId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getClaimsUnderSection21(
        currentProjectId,
        currentProposalId
      );

      if (response?.status === 200) {
        setRecords(response.data || []);
      }
    } catch (err) {
      console.error("Error fetching Section 21 claims:", err);
      setError("Error fetching Section 21 claims: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    if (!currentProjectId || !currentProposalId) return;

    try {
      setLoadingRecords(true);
      const response = await apiService.getProposalById(currentProjectId, currentProposalId);
      setProposalData(response.data);
    } catch (err) {
      setError('Failed to fetch data: ' + err.message);
    } finally {
      setLoadingRecords(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentProjectId, currentProposalId]);

  useEffect(() => {
    if (currentProjectId && currentProposalId) {
      fetchClaims();
    }
  }, [currentProjectId, currentProposalId]);

  const handleViewDocument = (urlOrFile) => {
    if (!urlOrFile) return;

    let url, isPdf;
    if (urlOrFile instanceof File) {
      url = URL.createObjectURL(urlOrFile);
      isPdf = urlOrFile.type === "application/pdf";
    } else {
      url = urlOrFile;
      isPdf = urlOrFile.toLowerCase().includes(".pdf");
    }

    setFileUrlToView(url);
    setIsPDFToView(isPdf);
    setShowDocumentViewer(true);
  };

  // Update handleAddNew to accept Village Context
  const handleAddNew = (villageContext = null) => {
    setIsEditMode(false);
    setFormData({
      claimant_name: '',
      claimant_address: '',
      claim_date: '',
      survey_no: '',
      gateNo: '', 
      claim_details: '',
      dist_name: villageContext ? villageContext.dist_name : (proposalData?.dist_name || ''),
      taluka_name: villageContext ? villageContext.taluka_name : (proposalData?.taluka_name || ''),
      village_name: villageContext ? villageContext.village_name : (proposalData?.village_name || ''),
      document: null
    });
    setOwners([]);
    setSelectedOwnerId("");
    setShowModal(true);
  };

  const handleViewDetails = async (record) => {
    try {
      setLoadingId(record.claimant_id || record.claimantId);
      setDetailsData(record);
      setShowDetailsModal(true);
      setNotification({ show: true, type: 'success', message: 'Claim details loaded successfully' });
    } catch (error) {
      setNotification({ show: true, type: 'error', message: 'Failed to load claim details' });
    } finally {
      setLoadingId(null);
    }
  };

  const handleSave = async (claimData) => {
    if (!selectedDecisionDate || !laoDecision || !laoRemarks) {
      setNotification({ show: true, type: 'error', message: 'Please fill all required fields' });
      return;
    }

    setIsProcessingDecision(true);
    try {
      const updateData = {
        lao_decision: laoDecision,
        last_upd_dt: selectedDecisionDate,
        claim_details: laoRemarks,
      };

      await apiService.patchUpdateClaimsUnderSection21(
        claimData.claimant_id || claimData.claimantId,
        updateData
      );

      const updatedRecords = records.map((rec) => {
        const recId = rec.claimant_id || rec.claimantId;
        const currentId = claimData.claimant_id || claimData.claimantId;
        return recId === currentId ? { ...rec, ...updateData } : rec;
      });

      setRecords(updatedRecords);
      setDetailsData({ ...claimData, ...updateData });
      setNotification({ show: true, type: 'success', message: 'LAO decision updated successfully' });

      setLaoDecision('');
      setSelectedDecisionDate('');
      setLaoRemarks('');
    } catch (error) {
      setNotification({ show: true, type: 'error', message: 'Failed to update LAO decision' });
    } finally {
      setIsProcessingDecision(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'document') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Grouped Printing Function
  const handlePrint = () => {
    if (!records || records.length === 0) {
      alert("No claim data available to print.");
      return;
    }

    const bhusampadanNo = proposalData?.section_11_bhusampadan_no || "—";
    const dtvList = proposalData?.dtv || [];

    let tablesHtml = "";

    if (dtvList.length > 0) {
      dtvList.forEach((dtvItem) => {
        const villageRecords = records.filter(rec => rec.village_name === dtvItem.village_name);
        if(villageRecords.length > 0) {
          tablesHtml += `
            <div class="village-header">गाव: ${dtvItem.village_name} | तालुका: ${dtvItem.taluka_name} | जिल्हा: ${dtvItem.dist_name}</div>
            ${generatePrintTableHtml(villageRecords)}
          `;
        }
      });
    } else {
       tablesHtml = `
          <div class="village-header">
            गाव: ${proposalData?.village_name || "—"} | 
            तालुका: ${proposalData?.taluka_name || "—"} | 
            जिल्हा: ${proposalData?.dist_name || "—"}
          </div>
          ${generatePrintTableHtml(records)}
       `;
    }

    const win = window.open("", "", "height=900,width=1200");
    win.document.write(`
      <html>
        <head>
          <title>Section 21 Claims List</title>
          <style>
             @page { size: A4 portrait; margin: 0.5in; }
             body { font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif; }
             table { border-collapse: collapse; width: 100%; margin-top: 10px; margin-bottom: 20px; }
             th, td { border: 1px solid black; padding: 6px; text-align: center; font-size: 12px; }
             .title { text-align: center; font-weight: bold; font-size: 18px; margin-bottom: 15px; } 
             .details { text-align: center; margin-bottom: 15px; line-height: 1.6; }
             .bhusampadan { font-weight: bold; font-size: 14px; }
             .village-header { font-weight: bold; background-color: #f0f0f0; padding: 8px; border: 1px solid black; border-bottom: none; margin-top: 20px;}
          </style>
        </head>
        <body>
          <div class="details">
            <div class="bhusampadan">भूसंपादन क्रमांक: ${bhusampadanNo}</div>
          </div>
          <div class="title">${translations[language]?.ClaimsUnderSection21 || "कलम २१ अंतर्गत दावे"}</div>
          ${tablesHtml}
        </body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  const generatePrintTableHtml = (recordsToPrint) => {
    return `
      <table>
        <thead>
          <tr>
            <th>${translations[language]?.SrNo21 || "अ.क्र"}</th>
            <th>${translations[language]?.ClaimantName || "दावा दाखल करणाऱ्याचे नाव"}</th>
            <th>${translations[language]?.Address21 || "पत्ता"}</th>
            <th>${translations[language]?.ClaimFilingDate || "दाखल तारीख"}</th>
            <th>${translations[language]?.ClaimGroupNumber || "गट क्रमांक"}</th>
            <th>${translations[language]?.ClaimDetails || "दाव्यातील तपशील"}</th>
            <th>${translations[language]?.LAODecisionStatus || "निर्णय स्थिती"}</th>
          </tr>
        </thead>
        <tbody>
          ${recordsToPrint.map((rec, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${rec.claimant_name || 'N/A'}</td>
              <td>${rec.claimant_address || 'N/A'}</td>
              <td>${rec.claim_date ? new Date(rec.claim_date).toLocaleDateString("hi-IN") : 'N/A'}</td>
              <td>${rec.survey_no || 'N/A'}</td>
              <td>${rec.claim_details || 'N/A'}</td>
              <td>${getStatusText(rec.lao_decision)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentProjectId || !currentProposalId) {
      alert('Project ID and Proposal ID are required');
      return;
    }

    setSubmitLoading(true);
    setError(null);

    try {
      let response;
      if (isEditMode) {
        const formDataToSend = new FormData();
        Object.keys(formData).forEach((key) => {
          if (formData[key]) {
            if (key === "gateNo") formDataToSend.append("survey_no", formData[key]);
            else formDataToSend.append(key, formData[key]);
          }
        });

        response = await apiService.patchUpdateClaimsUnderSection21(
          selectedRecord.claimantId || selectedRecord.claimant_id,
          formDataToSend
        );
      } else {
        const fd = new FormData();
        fd.append('project_id', currentProjectId);
        fd.append('proposal_id', currentProposalId);

        Object.entries(formData).forEach(([key, value]) => {
          if (value !== null && value !== '') {
            if (key === "gateNo") fd.append("survey_no", value);
            else fd.append(key, value);
          }
        });

        response = await apiService.postAddClaimsUnderSection21(fd);
      }

      if (response?.status === 200) {
        setShowModal(false);
        fetchClaims();
        alert(isEditMode ? 'दावा सफलतापूर्वक अपडेट किया गया!' : 'दावा सफलतापूर्वक जोड़ा गया!');
      } else {
        setError(response?.message || 'Failed to save claim');
      }
    } catch (err) {
      setError('Error saving claim: ' + (err.message || err));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      claimant_name: '',
      claimant_address: '',
      claim_date: '',
      survey_no: '',
      gateNo: '',
      claim_details: '',
      dist_name: '',
      taluka_name: '',
      village_name: '',
      document: null
    });
    setSelectedRecord(null);
    setIsEditMode(false);
  };

  const getStatusIcon = (decision) => {
    if (decision === 'approved' || decision === 'completed') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (decision === 'rejected') return <X className="h-5 w-5 text-red-600" />;
    if (decision === 'pending_documents') return <FileText className="h-5 w-5 text-orange-600" />;
    if (decision === 'under_review') return <AlertCircle className="h-5 w-5 text-blue-600" />;
    return <Clock className="h-5 w-5 text-yellow-600" />;
  };

  const getStatusText = (decision) => {
    switch (decision) {
      case 'approved': return 'स्वीकृत';
      case 'rejected': return 'अस्वीकृत';
      case 'pending_documents': return 'दस्तावेज रलंबित';
      case 'under_review': return 'समीक्षाधीन';
      case 'completed': return 'पूर्ण';
      default: return 'प्रलंबित';
    }
  };

  const getStatusColor = (decision) => {
    switch (decision) {
      case 'approved':
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'pending_documents': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'under_review': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const renderDocumentViewer = () => {
    if (!showDocumentViewer || !fileUrlToView) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[98vh] overflow-hidden">
          <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
            <h3 className="text-lg font-semibold">दस्तावेज पूर्वावलोकन</h3>
            <button onClick={() => setShowDocumentViewer(false)} className="hover:text-gray-200">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="p-4 h-[86vh] overflow-auto">
            {isPDFToView ? (
              <iframe src={fileUrlToView} className="w-full h-full" title="PDF Viewer" />
            ) : (
              <img src={fileUrlToView} alt="दस्तावेज़" className="max-h-[75vh] mx-auto" />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDetailsModal = () => {
     if (!showDetailsModal || !detailsData) return null;
     return (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
         <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
           <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-6 flex justify-between">
              <h3 className="text-2xl font-bold text-white">{translations[language]?.Section21ClaimDetails}</h3>
              <button onClick={() => setShowDetailsModal(false)} className="text-white hover:text-gray-200">
                <X className="h-6 w-6" />
              </button>
           </div>
           <div className="p-8 bg-gray-50 border-b border-gray-200">
             <p className="text-gray-900 font-medium">Claimant: {detailsData.claimant_name}</p>
             <p className="text-gray-900 font-medium">Survey: {detailsData.survey_no}</p>
             <p className="text-gray-900 font-medium">Address: {detailsData.claimant_address}</p>
             <p className="text-gray-900 font-medium">Decision: {getStatusText(detailsData.lao_decision)}</p>
           </div>
         </div>
       </div>
     );
  };

  const renderTable = (recordsList) => (
    <table className="w-full border border-gray-200 border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
      <thead>
        <tr className="bg-gray-100 border-b border-gray-200">
          <th className="border-r border-gray-200 p-3 text-center font-medium text-gray-700">{translations[language]?.Proceedings}</th>
          <th className="border-r border-gray-200 p-3 text-left font-medium text-gray-700">{translations[language]?.ClaimantName}</th>
          <th className="border-r border-gray-200 p-3 text-left font-medium text-gray-700">{translations[language]?.Address21}</th>
          <th className="border-r border-gray-200 p-3 text-center font-medium text-gray-700">{translations[language]?.ClaimFilingDate}</th>
          <th className="border-r border-gray-200 p-3 text-center font-medium text-gray-700">{translations[language]?.ClaimGroupNumber}</th>
          <th className="border-r border-gray-200 p-3 text-left font-medium text-gray-700">{translations[language]?.ClaimDetails}</th>
          <th className="border-r border-gray-200 p-3 text-center font-medium text-gray-700">{translations[language]?.LAODecisionStatus}</th>
        </tr>
      </thead>
      <tbody>
        {recordsList.length > 0 ? (
          recordsList.map((rec, index) => (
            <tr key={rec.claimant_id || rec.claimantId || index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <td className="p-3 text-center border-r border-gray-200">
                <button
                  onClick={async () => {
                    setLoadingId(rec.claimant_id || rec.claimantId);
                    await handleViewDetails(rec);
                  }}
                  disabled={loadingId === (rec.claimant_id || rec.claimantId)}
                  className="p-2 bg-green-600 hover:bg-green-700 text-white rounded transition-all duration-300 shadow-sm disabled:opacity-50"
                  title="विवरण देखें"
                >
                  {loadingId === (rec.claimant_id || rec.claimantId) ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                </button>
              </td>
              <td className="border-r border-gray-200 p-3">{rec.claimant_name}</td>
              <td className="border-r border-gray-200 p-3">{rec.claimant_address}</td>
              <td className="border-r border-gray-200 p-3 text-center">
                {new Date(rec.claim_date).toLocaleDateString("hi-IN")}
              </td>
              <td className="border-r border-gray-200 p-3 text-center">{rec.survey_no}</td>
              <td className="border-r border-gray-200 p-3">
                <div className="max-w-xs truncate" title={rec.claim_details}>{rec.claim_details}</div>
              </td>
              <td className="border-r border-gray-200 p-3 text-center">
                <div className="flex items-center justify-center space-x-2">
                  {getStatusIcon(rec.lao_decision)}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(rec.lao_decision)}`}>
                    {getStatusText(rec.lao_decision)}
                  </span>
                </div>
              </td>
            </tr>
          ))
        ) : (
          !loading && (
            <tr>
              <td colSpan="7" className="text-center p-8 text-gray-500">
                <div className="flex flex-col items-center">
                  <FileText className="h-12 w-12 text-gray-300 mb-2" />
                  <p>{translations[language]?.NoClaimsFound || "No Claims Found"}</p>
                </div>
              </td>
            </tr>
          )
        )}
      </tbody>
    </table>
  );

  if (!currentProjectId || !currentProposalId) {
    return (
      <div className="p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <div className="text-lg mt-2">{translations[language]?.LoadingProjectInfo}</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Global Header & Print Button */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-center p-2 space-y-1">
              <p className="font-semibold text-lg">
                भूसंपादन प्रकरण क्रमांक: {proposalData?.section_11_bhusampadan_no || "—"}
              </p>
              <p className="text-gray-600 text-sm">
                {proposalData?.dtv && proposalData.dtv.length > 1 
                  ? "अनेक गावे समाविष्ट" 
                  : `मौजे ${proposalData?.village_name || "—"} | ता. ${proposalData?.taluka_name || "—"} | जि. ${proposalData?.dist_name || "—"}`}
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-800">
              {translations[language]?.ClaimsUnderSection21}
            </h2>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center space-x-2 transition-all duration-300 shadow-sm"
              >
                <Printer className="h-4 w-4" />
                <span>{translations[language]?.Print21 || "Print"}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">{translations[language]?.LoadingClaims}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* --- VILLAGE-WISE SPLIT RENDER --- */}
      {proposalData?.dtv && proposalData.dtv.length > 0 ? (
        proposalData.dtv.map((dtvItem, index) => {
          const villageRecords = records.filter((rec) => rec.village_name === dtvItem.village_name);

          return (
            <div key={dtvItem.dtv_id || index} className="bg-white rounded-lg shadow-sm border mb-8">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                     <span className="text-blue-600">गाव:</span> {dtvItem.village_name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    ता. {dtvItem.taluka_name} | जि. {dtvItem.dist_name}
                  </p>
                </div>
                
                {(userRole === 3 || userRole === 14) && (
                  <button
                    onClick={() => handleAddNew(dtvItem)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2 transition-all duration-300 shadow-sm"
                  >
                    <Plus className="h-4 w-4" />
                    <span>{translations[language]?.AddNewClaim || "Add New Claim"}</span>
                  </button>
                )}
              </div>
              <div className="p-4 overflow-x-auto">
                {renderTable(villageRecords)}
              </div>
            </div>
          );
        })
      ) : (
        <div className="bg-white rounded-lg shadow-sm border">
           <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-800">सर्व दावे</h3>
              {(userRole === 3 || userRole === 14) && (
                <button
                  onClick={() => handleAddNew()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2 transition-all duration-300 shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>{translations[language]?.AddNewClaim || "Add New Claim"}</span>
                </button>
              )}
            </div>
          <div className="p-4 overflow-x-auto">
            {renderTable(records)}
          </div>
        </div>
      )}


      {/* Add/Edit Claim Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {isEditMode ? 'दावा संपादित करें' : <span>{translations[language]?.AddNewClaim}</span>}
              </h3>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700 p-1">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Typed Gat No */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language]?.ClaimGroupNumber} (Gat No) *
                  </label>
                  <input
                    type="text"
                    name="gateNo"
                    value={formData.gateNo}
                    onChange={handleInputChange}
                    placeholder="Enter Gat No"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language]?.ClaimantName} *
                  </label>
                  {owners.length > 0 ? (
                    <select
                      value={selectedOwnerId}
                      onChange={handleOwnerSelect}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">-- Select Owner --</option>
                      {owners.map(owner => (
                        <option key={owner.land_owner_id} value={owner.land_owner_id}>
                          {owner.owner_name}
                        </option>
                      ))}
                    </select>
                  ) : ownerMessage ? (
                    <div className="text-red-600 text-sm font-medium mt-1">{ownerMessage}</div>
                  ) : (
                    <input
                      type="text"
                      name="claimant_name"
                      value={formData.claimant_name}
                      onChange={handleInputChange}
                      placeholder="Enter claimant's full name"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      required
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language]?.Address21} *
                  </label>
                  <input
                    type="text"
                    name="claimant_address"
                    value={formData.claimant_address}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language]?.ClaimFilingDate} *
                  </label>
                  <input
                    type="date"
                    name="claim_date"
                    value={formData.claim_date}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                
                {/* Visual indicator of the locked village for the claim */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">गाव (Village)</label>
                  <input 
                    type="text" 
                    disabled 
                    value={formData.village_name} 
                    className="w-full border border-gray-200 bg-gray-100 rounded-lg px-3 py-2 text-gray-500 cursor-not-allowed" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language]?.Documents21}
                  </label>
                  <input
                    type="file"
                    name="document"
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    accept=".pdf,.doc,.docx,.jpg,.png"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations[language]?.ClaimDetails} *
                </label>
                <textarea
                  name="claim_details"
                  value={formData.claim_details}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {translations[language]?.Cancel21}
                </button>
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {submitLoading ? 'प्रक्रिया...' : (isEditMode ? 'दावा अपडेट करें' : <span>{translations[language]?.AddClaim}</span>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {renderDetailsModal()}
      {renderDocumentViewer()}
    </div>
  );
};

export default UnderSection21;